import React, { useState, useMemo } from "react";
import _ from "lodash";
import VirtualizedDataTable from "./VirtualizedDataTable";
import SubmissionDataViewSelector from "../SubmissionDataViewSelector";
import SubmissionFileSelector from "../SubmissionFileSelector";
import ColumnHeaderMappingWarning from "../ColumnHeaderMappingWarning";
import TableLegend from "./TableLegend";

const DataOutputOriginal = ({ submissions }) => {
  const [selectedFileId, setSelectedFileId] = useState(submissions?.[0]?.fileId ?? null);
  const [selectedDataView, setSelectedDataView] = useState("all");

  const attachment = useMemo(() => {
    const a = submissions.find((s) => s.fileId === selectedFileId) ?? submissions[0];
    setSelectedFileId(a?.fileId);
    return a;
  }, [submissions, selectedFileId]);

  const headers = useMemo(
    () => (attachment?.data?.length ? Object.keys(attachment.data[0]) : []),
    [attachment?.data]
  );

  const filteredData = useMemo(() => {
    const result = attachment?.originalData ?? [];
    switch (selectedDataView) {
      case "errors":
        return result.filter((row) => row.hasErrors);
      case "selected":
        return result.filter((row) => row.selected);
      case "unselected":
        return result.filter((row) => !row.selected);
      default:
        return result;
    }
  }, [attachment?.originalData, selectedDataView]);

  const mappedColumns = useMemo(
    () => new Set(_.values(attachment?.originalData[0]?.mapping)),
    [attachment?.originalData]
  );

  const columnDefs = useMemo(() => {
    if (!headers?.length) return [];
    return headers.map((header, index) => ({
      id: header,
      headerClassName: mappedColumns.has(header)
        ? "data-table__head__column data-table__head__column--mapped data-table__head__column--hoverable"
        : "data-table__head__column",
      header: (
        <ColumnHeaderMappingWarning
          columnName={header}
          columnLabel={header}
          warningFilenames={attachment?.mappingWarningsBySourceColumn[header]}
          lastColumn={index === headers.length - 1}
          showTooltip={true}
        />
      ),
      cell: (row) => {
        const getCellClassName = () => {
          if (!row.selected) return "data-table__row__item";
          const schemaColumnName = _.findKey(row.mapping, (val) => val === header);
          const isMapped = _.values(row.mapping).includes(header);

          if (schemaColumnName && row.columns_with_errors[schemaColumnName]) {
            return "data-table__row__item data-table__row__item--invalid";
          }
          if (isMapped) return "data-table__row__item data-table__row__item--mapped";
          return "data-table__row__item data-table__row__item--selected";
        };
        return <td className={getCellClassName()}>{row.data[header]}</td>;
      },
    }));
  }, [headers, mappedColumns, attachment]);

  return (
    <>
      <div className="t--upcase t--sm t--subtitle t--500 mb--8">View Filters</div>
      <div className="submission-selector__container mb--16">
        <div className="flex gap--16">
          <SubmissionDataViewSelector
            selectedDataView={selectedDataView}
            setSelectedDataView={setSelectedDataView}
            visibleOptions={["all", "errors", "selected", "unselected"]}
          />
          <SubmissionFileSelector
            submissions={submissions}
            selectedFile={selectedFileId}
            setSelectedFile={setSelectedFileId}
            hideAllFiles={true}
          />
        </div>
        <TableLegend view="original" />
      </div>
      <VirtualizedDataTable
        data={filteredData}
        columns={columnDefs}
        getRowClassName={() => "data-table__row"}
        minRows={15}
      />
    </>
  );
};

export default DataOutputOriginal;
